import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import Sidebar from './Sidebar';
import MetaTag from '../layout/MetaTag';
import Loader from '../layout/loader';
import { getCategoryIcon } from '../components/marketplace/CategoryIcons';
import './allSearchKeywords.css';
import './adminRoutes.css';

// Material-UI Icons & Components
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Chip
} from '@material-ui/core';

function AllSearchedKeywords() {
  const [keywords, setKeywords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    totalKeywords: 0,
    todaySearches: 0,
    thisWeekSearches: 0,
    unsentKeywords: 0,
    topKeywords: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'new' | 'sent' | 'unsent'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOption, setSortOption] = useState('most_searched'); // 'most_searched' | 'newest' | 'oldest' | 'alphabetical'

  // Selection State
  const [selectedIds, setSelectedIds] = useState([]);

  // Detail Drawer State
  const [drawerKeywordId, setDrawerKeywordId] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Row Action Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTargetKeyword, setMenuTargetKeyword] = useState(null);

  // Header More Options Menu
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);

  // "Send to Sellers" Wizard Modal State
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendTargetKeywords, setSendTargetKeywords] = useState([]);
  const [sendTargetType, setSendTargetType] = useState('all_relevant'); // 'all_relevant' | 'by_category' | 'selected_sellers'
  const [sendCategory, setSendCategory] = useState('');
  const [sendCustomMessage, setSendCustomMessage] = useState('');
  const [availableSellers, setAvailableSellers] = useState([]);
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  const [sellerSearchQuery, setSellerSearchQuery] = useState('');
  const [sellerLoading, setSellerLoading] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  // Classify Modal State
  const [isClassifyModalOpen, setIsClassifyModalOpen] = useState(false);
  const [classifyKeyword, setClassifyKeyword] = useState(null);
  const [classifyCategoryName, setClassifyCategoryName] = useState('');

  // Delete All Modal State
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [deleteAllConfirmInput, setDeleteAllConfirmInput] = useState('');

  // 1. Fetch All Keywords, Stats, and Categories
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [kwRes, statsRes, catRes] = await Promise.all([
        axios.get('/api/search/find/allkeywords'),
        axios.get('/api/admin/search/keywords/stats'),
        axios.get('/api/categories?status=all')
      ]);

      const kwList = kwRes.data.keywords || kwRes.data.msg || [];
      setKeywords(kwList);
      setStats(statsRes.data.stats || {
        totalKeywords: kwList.length,
        todaySearches: 0,
        thisWeekSearches: 0,
        unsentKeywords: kwList.filter(k => k.status === 'unsent').length,
        topKeywords: kwList.slice(0, 5)
      });
      setCategories(catRes.data.categories || []);
    } catch (err) {
      console.error("Failed to fetch search keywords:", err);
      setError("Unable to load search keywords. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 2. Fetch Keyword Details when Drawer is Open
  useEffect(() => {
    if (!drawerKeywordId) {
      setDrawerData(null);
      return;
    }
    const fetchDetail = async () => {
      try {
        setDrawerLoading(true);
        const res = await axios.get(`/api/admin/search/keywords/${drawerKeywordId}`);
        if (res.data && res.data.success) {
          setDrawerData(res.data.keyword);
        }
      } catch (err) {
        console.error("Failed to load keyword detail:", err);
      } finally {
        setDrawerLoading(false);
      }
    };
    fetchDetail();
  }, [drawerKeywordId]);

  // 3. Fetch Sellers when entering seller selection mode
  useEffect(() => {
    if (!isSendModalOpen) return;
    const fetchSellers = async () => {
      try {
        setSellerLoading(true);
        const res = await axios.get(`/api/admin/search/keywords/match-sellers?category=${encodeURIComponent(sendCategory || 'all')}&search=${encodeURIComponent(sellerSearchQuery || '')}`);
        if (res.data?.stores) {
          setAvailableSellers(res.data.stores);
        }
      } catch (err) {
        console.error("Failed to load sellers:", err);
      } finally {
        setSellerLoading(false);
      }
    };
    fetchSellers();
  }, [isSendModalOpen, sendCategory, sellerSearchQuery]);

  // Filtered & Sorted Keywords
  const filteredKeywords = useMemo(() => {
    let list = [...keywords];
    const q = searchQuery.toLowerCase().trim();

    if (q) {
      list = list.filter(k =>
        k.searchKeywords?.toLowerCase().includes(q) ||
        k.category?.toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'new') {
      const twoDaysAgo = new Date(Date.now() - 48 * 60 * 60 * 1000);
      list = list.filter(k => k.status === 'unsent' && new Date(k.createdAt || k.firstSearchedAt) >= twoDaysAgo);
    } else if (statusFilter === 'sent') {
      list = list.filter(k => k.status === 'sent');
    } else if (statusFilter === 'unsent') {
      list = list.filter(k => k.status === 'unsent' || !k.status);
    }

    if (categoryFilter !== 'all') {
      list = list.filter(k => k.category === categoryFilter);
    }

    // Sort
    if (sortOption === 'most_searched') {
      list.sort((a, b) => (b.count || 1) - (a.count || 1));
    } else if (sortOption === 'newest') {
      list.sort((a, b) => new Date(b.lastSearchedAt || b.createdAt) - new Date(a.lastSearchedAt || a.createdAt));
    } else if (sortOption === 'oldest') {
      list.sort((a, b) => new Date(a.firstSearchedAt || a.createdAt) - new Date(b.firstSearchedAt || b.createdAt));
    } else if (sortOption === 'alphabetical') {
      list.sort((a, b) => (a.searchKeywords || '').localeCompare(b.searchKeywords || ''));
    }

    return list;
  }, [keywords, searchQuery, statusFilter, categoryFilter, sortOption]);

  // Selection Handlers
  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredKeywords.map(k => k._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Row Context Menu
  const handleOpenRowMenu = (e, kw) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuTargetKeyword(kw);
  };

  const handleCloseRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuTargetKeyword(null);
  };

  // Single Delete
  const handleDeleteSingle = async (kw) => {
    handleCloseRowMenu();
    const willDelete = await swal({
      title: "Delete keyword?",
      text: `Are you sure you want to delete "${kw.searchKeywords}"? This action cannot be undone.`,
      icon: "warning",
      buttons: ["Cancel", "Delete Keyword"],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        await axios.delete(`/api/admin/search/keywords/${kw._id}`);
        swal("Deleted", `Keyword "${kw.searchKeywords}" removed.`, "success");
        setSelectedIds(prev => prev.filter(id => id !== kw._id));
        if (drawerKeywordId === kw._id) setDrawerKeywordId(null);
        loadData();
      } catch (err) {
        swal("Error", err.response?.data?.msg || "Failed to delete keyword.", "error");
      }
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    const willDelete = await swal({
      title: `Delete ${selectedIds.length} keywords?`,
      text: `This will permanently remove the ${selectedIds.length} selected search keywords.`,
      icon: "warning",
      buttons: ["Cancel", `Delete ${selectedIds.length} Keywords`],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        const res = await axios.post('/api/admin/search/keywords/bulk-delete', {
          ids: selectedIds
        });
        swal("Deleted", res.data.message || "Selected keywords deleted.", "success");
        setSelectedIds([]);
        loadData();
      } catch (err) {
        swal("Error", err.response?.data?.msg || "Bulk deletion failed.", "error");
      }
    }
  };

  // Delete ALL Keywords
  const handleExecuteDeleteAll = async (e) => {
    e.preventDefault();
    if (deleteAllConfirmInput !== 'DELETE') {
      return swal("Validation", 'Please type "DELETE" to confirm.', "warning");
    }

    try {
      const res = await axios.post('/api/admin/search/keywords/delete-all', {
        confirm: 'DELETE'
      });
      swal("Purged", res.data.message || "All search keywords have been deleted.", "success");
      setIsDeleteAllModalOpen(false);
      setDeleteAllConfirmInput('');
      setSelectedIds([]);
      loadData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to delete all keywords.", "error");
    }
  };

  // Send to Sellers Modal Workflow
  const handleOpenSendModal = (keywordsToSend = null) => {
    handleCloseRowMenu();
    let targetList = [];
    if (keywordsToSend && Array.isArray(keywordsToSend)) {
      targetList = keywordsToSend;
    } else if (keywordsToSend && keywordsToSend._id) {
      targetList = [keywordsToSend];
    } else if (selectedIds.length > 0) {
      targetList = keywords.filter(k => selectedIds.includes(k._id));
    } else {
      targetList = filteredKeywords.slice(0, 10);
    }

    if (targetList.length === 0) {
      return swal("Selection", "Please select at least one keyword to send to sellers.", "info");
    }

    setSendTargetKeywords(targetList);
    setSendTargetType('all_relevant');
    setSendCategory(targetList[0]?.category || 'all');
    setSelectedSellerIds([]);
    setSendCustomMessage('');
    setIsSendModalOpen(true);
  };

  const handleRemoveSendKeyword = (idToRemove) => {
    const next = sendTargetKeywords.filter(k => k._id !== idToRemove);
    if (next.length === 0) {
      setIsSendModalOpen(false);
    } else {
      setSendTargetKeywords(next);
    }
  };

  const handleExecuteSendDemandAlert = async () => {
    if (sendTargetKeywords.length === 0) return;
    try {
      setSendingAlert(true);
      const payload = {
        keywordIds: sendTargetKeywords.map(k => k._id),
        targetType: sendTargetType,
        category: sendCategory,
        sellerIds: sendTargetType === 'selected_sellers' ? selectedSellerIds : undefined,
        customMessage: sendCustomMessage.trim() || undefined
      };

      const res = await axios.post('/api/admin/search/keywords/send-to-sellers', payload);
      swal("Demand Alert Sent", res.data.message || "Demand notifications dispatched to sellers.", "success");
      setIsSendModalOpen(false);
      setSelectedIds([]);
      loadData();
      if (drawerKeywordId) {
        // Refresh drawer
        setDrawerKeywordId(drawerKeywordId);
      }
    } catch (err) {
      swal("Failed to Send", err.response?.data?.msg || "Could not dispatch notifications.", "error");
    } finally {
      setSendingAlert(false);
    }
  };

  // Classify Keyword
  const handleOpenClassifyModal = (kw) => {
    handleCloseRowMenu();
    setClassifyKeyword(kw);
    setClassifyCategoryName(kw.category || 'Other');
    setIsClassifyModalOpen(true);
  };

  const handleExecuteClassify = async (e) => {
    e.preventDefault();
    if (!classifyKeyword || !classifyCategoryName) return;

    try {
      const selectedCat = categories.find(c => c.name === classifyCategoryName);
      await axios.put(`/api/admin/search/keywords/${classifyKeyword._id}/classify`, {
        category: classifyCategoryName,
        categoryId: selectedCat?._id || undefined
      });

      swal("Classified", `Keyword "${classifyKeyword.searchKeywords}" updated.`, "success");
      setIsClassifyModalOpen(false);
      loadData();
    } catch (err) {
      swal("Error", err.response?.data?.msg || "Failed to classify category.", "error");
    }
  };

  const formatLastSeen = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="dashboard-container">
      <MetaTag
        title="Search Keywords | IMSHOP Admin"
        description="Marketplace customer search keywords and demand signals"
      />
      <Sidebar />

      <main className="main-container admin-dashboard-main">
        {/* ═══════════════════════ 1. PAGE HEADER ═══════════════════════ */}
        <div className="im-kw-header">
          <div className="im-kw-header-left">
            <span className="im-kw-eyebrow">MARKETING & DEMAND INTELLIGENCE</span>
            <h1 className="im-kw-title">Search Keywords</h1>
            <p className="im-kw-desc">
              Understand what customers are searching for and share relevant demand signals with sellers.
            </p>
          </div>

          <div className="im-kw-header-actions">
            <Button
              variant="outlined"
              onClick={loadData}
              startIcon={<RefreshIcon />}
              style={{ fontSize: "12.5px", textTransform: "none", borderColor: "#d1d5db", background: "#ffffff", padding: "7px 14px", borderRadius: "8px" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOpenSendModal()}
              startIcon={<SendOutlinedIcon />}
              style={{ background: "#8b2626", color: "#ffffff", fontSize: "12.5px", fontWeight: "800", textTransform: "none", padding: "7.5px 16px", borderRadius: "8px" }}
            >
              + Send to Sellers
            </Button>
            <button
              type="button"
              onClick={(e) => setHeaderMenuAnchorEl(e.currentTarget)}
              style={{ background: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "7px 10px", cursor: "pointer", color: "#4b5563" }}
              title="More Actions"
            >
              <MoreVertIcon fontSize="small" />
            </button>
          </div>
        </div>

        {/* Header More Options Menu */}
        <Menu
          anchorEl={headerMenuAnchorEl}
          keepMounted
          open={Boolean(headerMenuAnchorEl)}
          onClose={() => setHeaderMenuAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setHeaderMenuAnchorEl(null);
              setDeleteAllConfirmInput('');
              setIsDeleteAllModalOpen(true);
            }}
            style={{ color: "#dc2626", fontSize: "13px", fontWeight: "700" }}
          >
            <DeleteOutlineIcon fontSize="small" style={{ marginRight: "8px" }} />
            Delete All Keywords
          </MenuItem>
        </Menu>

        {/* ═══════════════════════ 2. LIVE SUMMARY STATS ═══════════════════════ */}
        <div className="im-kw-stats-grid">
          <div className="im-kw-stat-card">
            <div className="im-kw-stat-icon" style={{ background: "#fdf6f5", color: "#8b2626" }}>
              <TrendingUpIcon />
            </div>
            <div className="im-kw-stat-meta">
              <span className="im-kw-stat-label">Total Keywords</span>
              <span className="im-kw-stat-val">{stats.totalKeywords.toLocaleString()}</span>
            </div>
          </div>

          <div className="im-kw-stat-card">
            <div className="im-kw-stat-icon" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              <TodayOutlinedIcon />
            </div>
            <div className="im-kw-stat-meta">
              <span className="im-kw-stat-label">Today Searches</span>
              <span className="im-kw-stat-val">{stats.todaySearches.toLocaleString()}</span>
            </div>
          </div>

          <div className="im-kw-stat-card">
            <div className="im-kw-stat-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>
              <DateRangeOutlinedIcon />
            </div>
            <div className="im-kw-stat-meta">
              <span className="im-kw-stat-label">This Week Searches</span>
              <span className="im-kw-stat-val">{stats.thisWeekSearches.toLocaleString()}</span>
            </div>
          </div>

          <div className="im-kw-stat-card">
            <div className="im-kw-stat-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
              <MarkEmailReadOutlinedIcon />
            </div>
            <div className="im-kw-stat-meta">
              <span className="im-kw-stat-label">Unsent Keywords</span>
              <span className="im-kw-stat-val">{stats.unsentKeywords.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* ═══════════════════════ 3. TOP DEMAND INSIGHTS BANNER ═══════════════════════ */}
        {stats.topKeywords && stats.topKeywords.length > 0 && (
          <div className="im-kw-demand-box">
            <div className="im-kw-demand-title">
              <TrendingUpIcon style={{ fontSize: "16px", color: "#8b2626" }} />
              <span>Top Customer Demand:</span>
            </div>
            <div className="im-kw-demand-chips">
              {stats.topKeywords.map((top, idx) => (
                <div
                  key={idx}
                  className="im-kw-demand-chip"
                  onClick={() => setSearchQuery(top.searchKeywords)}
                  title={`Filter by "${top.searchKeywords}"`}
                >
                  <span>{idx + 1}. {top.searchKeywords}</span>
                  <span className="im-kw-demand-count">{top.count || 1} searches</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════ 4. SEARCH & FILTER TOOLBAR ═══════════════════════ */}
        <div className="im-kw-toolbar">
          <div className="im-kw-search-wrap">
            <SearchIcon style={{ position: "absolute", left: "10px", top: "9px", color: "#9ca3af", fontSize: "19px" }} />
            <input
              type="text"
              className="im-kw-search-input"
              placeholder="Search keywords or categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="im-kw-filters-row">
            {['all', 'new', 'sent', 'unsent'].map(s => (
              <button
                key={s}
                type="button"
                className={`im-kw-pill-btn ${statusFilter === s ? 'im-kw-pill-btn--active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : s === 'new' ? 'New' : s === 'sent' ? 'Sent' : 'Unsent'}
              </button>
            ))}

            <select
              className="im-kw-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.filter(c => c.level === 1).map(c => (
                <option key={c._id} value={c.name}>{c.name}</option>
              ))}
              <option value="Other">Other</option>
            </select>

            <select
              className="im-kw-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="most_searched">Most Searched</option>
              <option value="newest">Newest Seen</option>
              <option value="oldest">Oldest Seen</option>
              <option value="alphabetical">Alphabetical (A-Z)</option>
            </select>
          </div>
        </div>

        {/* ═══════════════════════ 5. DATA TABLE & MOBILE CARDS ═══════════════════════ */}
        {loading ? (
          <div style={{ padding: "40px 0" }}>
            <Loader />
          </div>
        ) : error ? (
          <div style={{ background: "#ffffff", border: "1px solid #fed7d7", borderRadius: "12px", padding: "36px", textAlign: "center" }}>
            <WarningAmberOutlinedIcon style={{ fontSize: "40px", color: "#dc2626", marginBottom: "8px" }} />
            <h3 style={{ margin: "0 0 8px", fontSize: "16px", color: "#111315" }}>{error}</h3>
            <Button variant="contained" onClick={loadData} style={{ background: "#8b2626", color: "#ffffff", textTransform: "none" }}>
              Try Again
            </Button>
          </div>
        ) : filteredKeywords.length > 0 ? (
          <>
            {/* Desktop Responsive Table */}
            <div className="im-kw-table-card">
              <table className="im-kw-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.length > 0 && selectedIds.length === filteredKeywords.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredKeywords.length}
                        onChange={handleToggleSelectAll}
                      />
                    </th>
                    <th>Keyword</th>
                    <th>Category</th>
                    <th style={{ textAlign: "center" }}>Searches</th>
                    <th>Last Seen</th>
                    <th>Seller Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords.map(kw => {
                    const isSelected = selectedIds.includes(kw._id);
                    return (
                      <tr key={kw._id} className={isSelected ? 'im-kw-row--selected' : ''}>
                        <td style={{ textAlign: "center" }}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleToggleSelectRow(kw._id)}
                          />
                        </td>
                        <td>
                          <strong
                            onClick={() => setDrawerKeywordId(kw._id)}
                            style={{ color: "#111315", fontSize: "14px", cursor: "pointer", display: "inline-block" }}
                            title="Click to view details"
                          >
                            {kw.searchKeywords}
                          </strong>
                        </td>
                        <td>
                          <span className="im-kw-badge-cat">
                            {getCategoryIcon({ name: kw.category }, 14)}
                            <span>{kw.category || 'Other'}</span>
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <strong style={{ fontSize: "13.5px", color: "#111315" }}>
                            {kw.count || 1}
                          </strong>
                        </td>
                        <td style={{ color: "#4b5563", fontSize: "12.5px" }}>
                          {formatLastSeen(kw.lastSearchedAt || kw.createdAt)}
                        </td>
                        <td>
                          <span className={`im-kw-badge-status ${kw.status === 'sent' ? 'im-kw-badge-status--sent' : 'im-kw-badge-status--unsent'}`}>
                            {kw.status === 'sent' ? `Sent (${kw.sentToSellersCount || 0} sellers)` : 'Unsent'}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            onClick={(e) => handleOpenRowMenu(e, kw)}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#6b7280" }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards (Visible on mobile/tablet) */}
            <div className="im-kw-mobile-card-list">
              {filteredKeywords.map(kw => {
                const isSelected = selectedIds.includes(kw._id);
                return (
                  <div key={kw._id} className="im-kw-mobile-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(kw._id)}
                        />
                        <strong style={{ fontSize: "15px", color: "#111315" }} onClick={() => setDrawerKeywordId(kw._id)}>
                          {kw.searchKeywords}
                        </strong>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleOpenRowMenu(e, kw)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </button>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px", fontSize: "12.5px" }}>
                      <span className="im-kw-badge-cat">
                        {kw.category || 'Other'}
                      </span>
                      <strong style={{ color: "#111315" }}>{kw.count || 1} searches</strong>
                      <span className={`im-kw-badge-status ${kw.status === 'sent' ? 'im-kw-badge-status--sent' : 'im-kw-badge-status--unsent'}`}>
                        {kw.status === 'sent' ? 'Sent' : 'Unsent'}
                      </span>
                    </div>

                    <div style={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenSendModal(kw)}
                        style={{ fontSize: "11.5px", textTransform: "none" }}
                      >
                        Send to Sellers
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ background: "#ffffff", border: "1px solid #edf0f4", borderRadius: "12px", padding: "48px", textAlign: "center" }}>
            <SearchIcon style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "8px" }} />
            <h3 style={{ fontSize: "16px", fontWeight: "800", color: "#111315", margin: 0 }}>
              No customer search keywords yet
            </h3>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 16px" }}>
              Once customers start searching on IMSHOP, their search demand will appear here.
            </p>
            <Button
              variant="outlined"
              onClick={loadData}
              startIcon={<RefreshIcon />}
              style={{ fontSize: "12.5px", textTransform: "none", borderColor: "#d1d5db" }}
            >
              Refresh
            </Button>
          </div>
        )}

        {/* ═══════════════════════ 6. STICKY BULK ACTIONS BAR ═══════════════════════ */}
        {selectedIds.length > 0 && (
          <div className="im-kw-bulk-bar">
            <div className="im-kw-bulk-left">
              <span className="im-kw-bulk-count">{selectedIds.length} selected</span>
              <span style={{ fontSize: "12.5px", color: "#9ca3af" }}>Bulk actions:</span>
            </div>

            <div className="im-kw-bulk-actions">
              <Button
                variant="contained"
                size="small"
                onClick={() => handleOpenSendModal()}
                startIcon={<SendOutlinedIcon style={{ fontSize: "16px" }} />}
                style={{ background: "#8b2626", color: "#ffffff", fontSize: "12px", fontWeight: "800", textTransform: "none" }}
              >
                Send to Sellers
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkDelete}
                startIcon={<DeleteOutlineIcon style={{ fontSize: "16px" }} />}
                style={{ color: "#f87171", borderColor: "#7f1d1d", fontSize: "12px", textTransform: "none" }}
              >
                Delete Selected
              </Button>
              <button
                type="button"
                onClick={handleClearSelection}
                style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "12px", cursor: "pointer", marginLeft: "6px" }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════ 7. ROW ACTION MENU ═══════════════════════ */}
        <Menu
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseRowMenu}
        >
          <MenuItem onClick={() => {
            handleCloseRowMenu();
            setDrawerKeywordId(menuTargetKeyword?._id);
          }}>
            <VisibilityOutlinedIcon fontSize="small" style={{ marginRight: "8px" }} />
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleOpenSendModal(menuTargetKeyword)}>
            <SendOutlinedIcon fontSize="small" style={{ marginRight: "8px" }} />
            Send to Sellers
          </MenuItem>
          <MenuItem onClick={() => handleOpenClassifyModal(menuTargetKeyword)}>
            <EditOutlinedIcon fontSize="small" style={{ marginRight: "8px" }} />
            Classify / Edit Category
          </MenuItem>
          <MenuItem onClick={() => handleDeleteSingle(menuTargetKeyword)} style={{ color: "#dc2626" }}>
            <DeleteOutlineIcon fontSize="small" style={{ marginRight: "8px" }} />
            Delete Keyword
          </MenuItem>
        </Menu>

        {/* ═══════════════════════ 8. KEYWORD DETAIL DRAWER ═══════════════════════ */}
        {drawerKeywordId && (
          <div className="im-kw-drawer-overlay" onClick={() => setDrawerKeywordId(null)}>
            <div className="im-kw-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="im-kw-drawer-header">
                <div>
                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#8b2626", textTransform: "uppercase" }}>
                    SEARCH KEYWORD DETAILS
                  </span>
                  <h2 style={{ fontSize: "18px", fontWeight: "900", color: "#111315", margin: "2px 0 0" }}>
                    {drawerData?.searchKeywords || 'Loading...'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setDrawerKeywordId(null)}
                  style={{ background: "#f3f4f6", border: "none", borderRadius: "20px", padding: "6px", cursor: "pointer" }}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>

              <div className="im-kw-drawer-body">
                {drawerLoading ? (
                  <Loader />
                ) : drawerData ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" }}>
                      <div style={{ background: "#f9fafb", padding: "12px", borderRadius: "8px", textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "700" }}>TOTAL SEARCHES</span>
                        <strong style={{ display: "block", fontSize: "20px", color: "#111315", marginTop: "2px" }}>
                          {drawerData.count || 1}
                        </strong>
                      </div>
                      <div style={{ background: "#f9fafb", padding: "12px", borderRadius: "8px", textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "700" }}>CATEGORY</span>
                        <strong style={{ display: "block", fontSize: "14px", color: "#8b2626", marginTop: "4px" }}>
                          {drawerData.category || 'Other'}
                        </strong>
                      </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", marginBottom: "8px" }}>
                        Timeline
                      </h4>
                      <div style={{ fontSize: "13px", color: "#374151", display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div><strong>First Seen:</strong> {new Date(drawerData.firstSearchedAt || drawerData.createdAt).toLocaleString('en-IN')}</div>
                        <div><strong>Last Seen:</strong> {new Date(drawerData.lastSearchedAt || drawerData.createdAt).toLocaleString('en-IN')}</div>
                      </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <h4 style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", margin: 0 }}>
                          Seller Outreach History ({drawerData.sentHistory?.length || 0})
                        </h4>
                        <span className={`im-kw-badge-status ${drawerData.status === 'sent' ? 'im-kw-badge-status--sent' : 'im-kw-badge-status--unsent'}`}>
                          {drawerData.status === 'sent' ? `Sent (${drawerData.sentToSellersCount || 0})` : 'Unsent'}
                        </span>
                      </div>

                      {drawerData.sentHistory && drawerData.sentHistory.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {drawerData.sentHistory.map((h, i) => (
                            <div key={i} style={{ background: "#f9fafb", border: "1px solid #edf0f4", borderRadius: "8px", padding: "10px 12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <strong style={{ fontSize: "13px", color: "#111315" }}>{h.storeName || 'Seller Store'}</strong>
                                <span style={{ fontSize: "11px", color: "#6b7280" }}>{new Date(h.sentAt).toLocaleDateString()}</span>
                              </div>
                              <span style={{ fontSize: "11.5px", color: "#6b7280", display: "block" }}>{h.sellerEmail}</span>
                              <p style={{ fontSize: "12px", color: "#374151", margin: "4px 0 0", fontStyle: "italic" }}>
                                "{h.message}"
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: "12.5px", color: "#9ca3af" }}>
                          No demand alerts sent for this keyword yet.
                        </p>
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              <div className="im-kw-drawer-footer">
                <Button
                  variant="outlined"
                  onClick={() => handleOpenClassifyModal(drawerData)}
                  style={{ flex: 1, textTransform: "none", fontSize: "12.5px" }}
                >
                  Classify
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    const kw = drawerData;
                    setDrawerKeywordId(null);
                    handleOpenSendModal(kw);
                  }}
                  style={{ flex: 1.5, background: "#8b2626", color: "#ffffff", fontWeight: "800", textTransform: "none", fontSize: "12.5px" }}
                >
                  Send to Sellers
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════ 9. SEND TO SELLERS MODAL ═══════════════════════ */}
        <Dialog
          open={isSendModalOpen}
          onClose={() => !sendingAlert && setIsSendModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle style={{ borderBottom: "1px solid #edf0f4" }}>
            <strong>Send Customer Demand Signals to Sellers</strong>
          </DialogTitle>
          <DialogContent dividers style={{ padding: "20px 24px" }}>
            {/* Step 1: Selected Keywords */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                1. Selected Demand Keywords ({sendTargetKeywords.length})
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {sendTargetKeywords.map(kw => (
                  <Chip
                    key={kw._id}
                    label={`"${kw.searchKeywords}" (${kw.count || 1} searches)`}
                    onDelete={() => handleRemoveSendKeyword(kw._id)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </div>
            </div>

            {/* Step 2: Target Audience */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                2. Target Seller Audience
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                {[
                  { id: 'all_relevant', title: 'All Relevant Sellers', desc: 'Active sellers across catalog' },
                  { id: 'by_category', title: 'By Category', desc: 'Sellers matching specific department' },
                  { id: 'selected_sellers', title: 'Select Specific Sellers', desc: 'Choose from active store list' }
                ].map(opt => (
                  <div
                    key={opt.id}
                    onClick={() => setSendTargetType(opt.id)}
                    style={{
                      border: "1.5px solid",
                      borderColor: sendTargetType === opt.id ? "#8b2626" : "#e5e7eb",
                      background: sendTargetType === opt.id ? "#fdf6f5" : "#ffffff",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      cursor: "pointer",
                      transition: "all 120ms ease"
                    }}
                  >
                    <strong style={{ fontSize: "13px", color: sendTargetType === opt.id ? "#8b2626" : "#111315", display: "block" }}>
                      {opt.title}
                    </strong>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>{opt.desc}</span>
                  </div>
                ))}
              </div>

              {sendTargetType === 'by_category' && (
                <FormControl variant="outlined" size="small" fullWidth margin="dense">
                  <InputLabel>Target Category</InputLabel>
                  <Select
                    value={sendCategory}
                    onChange={(e) => setSendCategory(e.target.value)}
                    label="Target Category"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(c => (
                      <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {sendTargetType === 'selected_sellers' && (
                <div style={{ border: "1px solid #edf0f4", borderRadius: "8px", padding: "12px", background: "#f9fafb" }}>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder="Search active stores by name..."
                      value={sellerSearchQuery}
                      onChange={(e) => setSellerSearchQuery(e.target.value)}
                    />
                  </div>

                  {sellerLoading ? (
                    <Loader />
                  ) : availableSellers.length > 0 ? (
                    <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
                      {availableSellers.map(s => {
                        const isChecked = selectedSellerIds.includes(s._id);
                        return (
                          <div
                            key={s._id}
                            onClick={() => {
                              setSelectedSellerIds(prev =>
                                prev.includes(s._id) ? prev.filter(id => id !== s._id) : [...prev, s._id]
                              );
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              background: isChecked ? "#fdf6f5" : "#ffffff",
                              border: "1px solid",
                              borderColor: isChecked ? "#fecaca" : "#edf0f4",
                              padding: "6px 10px",
                              borderRadius: "6px",
                              cursor: "pointer"
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <Checkbox size="small" checked={isChecked} />
                              <div>
                                <strong style={{ fontSize: "12.5px", color: "#111315" }}>{s.name}</strong>
                                <span style={{ fontSize: "11px", color: "#6b7280", display: "block" }}>{s.category || 'General'} • {s.vendor?.email}</span>
                              </div>
                            </div>
                            <span style={{ fontSize: "10.5px", color: "#166534", background: "#dcfce7", padding: "1px 6px", borderRadius: "4px", fontWeight: "800" }}>
                              Active
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>No active sellers match search.</p>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: Message Composer */}
            <div>
              <label style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                3. Demand Notification Message
              </label>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                size="small"
                placeholder={`Customers are searching for "${sendTargetKeywords.map(k => k.searchKeywords).join(', ')}". Consider listing products in this category on IMSHOP to capture customer demand.`}
                value={sendCustomMessage}
                onChange={(e) => setSendCustomMessage(e.target.value)}
                helperText="Leave empty to send default customer demand alert."
              />
            </div>
          </DialogContent>
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button onClick={() => setIsSendModalOpen(false)} disabled={sendingAlert} style={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              onClick={handleExecuteSendDemandAlert}
              disabled={sendingAlert}
              variant="contained"
              style={{ background: "#8b2626", color: "#ffffff", fontWeight: "800", textTransform: "none" }}
            >
              {sendingAlert ? "Dispatching..." : "Send Notification"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ═══════════════════════ 10. CLASSIFY CATEGORY MODAL ═══════════════════════ */}
        <Dialog
          open={isClassifyModalOpen}
          onClose={() => setIsClassifyModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={handleExecuteClassify}>
            <DialogTitle style={{ borderBottom: "1px solid #edf0f4" }}>
              <strong>Classify Keyword Category</strong>
            </DialogTitle>
            <DialogContent dividers style={{ padding: "20px" }}>
              <p style={{ fontSize: "13px", color: "#4b5563", margin: "0 0 14px" }}>
                Assign keyword <strong>"{classifyKeyword?.searchKeywords}"</strong> to a marketplace taxonomy department:
              </p>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={classifyCategoryName}
                  onChange={(e) => setClassifyCategoryName(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="Other">Other</MenuItem>
                  {categories.map(c => (
                    <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions style={{ padding: "14px 20px" }}>
              <Button onClick={() => setIsClassifyModalOpen(false)} style={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ background: "#8b2626", color: "#ffffff", fontWeight: "800", textTransform: "none" }}>
                Save Classification
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ═══════════════════════ 11. DELETE ALL CONFIRMATION MODAL ═══════════════════════ */}
        <Dialog
          open={isDeleteAllModalOpen}
          onClose={() => setIsDeleteAllModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <form onSubmit={handleExecuteDeleteAll}>
            <DialogTitle style={{ borderBottom: "1px solid #edf0f4", color: "#dc2626" }}>
              <strong>Delete All Search Keywords?</strong>
            </DialogTitle>
            <DialogContent style={{ padding: "20px" }}>
              <p style={{ fontSize: "13px", color: "#4b5563", margin: "0 0 12px" }}>
                You are about to permanently delete <strong>{stats.totalKeywords.toLocaleString()} search keywords</strong> and all associated outreach history.
              </p>
              <p style={{ fontSize: "12.5px", color: "#dc2626", fontWeight: "700", margin: "0 0 12px" }}>
                This action cannot be undone. Type <strong>DELETE</strong> to confirm:
              </p>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Type DELETE"
                value={deleteAllConfirmInput}
                onChange={(e) => setDeleteAllConfirmInput(e.target.value)}
              />
            </DialogContent>
            <DialogActions style={{ padding: "14px 20px", borderTop: "1px solid #edf0f4" }}>
              <Button onClick={() => setIsDeleteAllModalOpen(false)} style={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={deleteAllConfirmInput !== 'DELETE'}
                variant="contained"
                style={{ background: deleteAllConfirmInput === 'DELETE' ? "#dc2626" : "#e5e7eb", color: "#ffffff", fontWeight: "800", textTransform: "none" }}
              >
                Delete Everything
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </main>
    </div>
  );
}

export default AllSearchedKeywords;
